/**
 * Data layer for `awin-integration-api-key-publisher-id`. Thin typed fetch
 * wrappers over /api/v1/awin-integration-api-key-publisher-id. Raw `fetch()`
 * is confined to this file per the dash contract (D6).
 *
 * BE shape (see apps/api/src/routes/awin-integration-api-key-publisher-id.ts):
 *   POST /   body: { publisher_id, api_key, label? }
 *   GET  /   -> { items: MaskedRow[] }
 *   DELETE /:publisher_id
 */

export interface CredentialRow {
	id: number;
	publisher_id: string;
	api_key_last4: string;
	label: string;
	status: string;
	created_at: number;
	updated_at: number;
}

export interface ListCredentialsResponse {
	items: CredentialRow[];
}

export interface CreateCredentialBody {
	publisher_id: string;
	api_key: string;
	label?: string;
}

export interface RevokeResponse {
	ok: boolean;
	publisher_id: string;
	status: string;
}

const BASE = "/api/v1/awin-integration-api-key-publisher-id";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function listCredentials(): Promise<ListCredentialsResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListCredentialsResponse>(res);
}

export async function createCredential(
	body: CreateCredentialBody,
): Promise<CredentialRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<CredentialRow>(res);
}

export async function revokeCredential(
	publisher_id: string,
): Promise<RevokeResponse> {
	const res = await fetch(`${BASE}/${encodeURIComponent(publisher_id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<RevokeResponse>(res);
}

/**
 * Validate API key: at least 16 chars, alphanumeric + dash/underscore only.
 * Partner Network API keys are opaque alphanumeric tokens.
 */
export function isValidApiKey(key: string): boolean {
	if (typeof key !== "string") return false;
	if (key.length < 16) return false;
	return /^[A-Za-z0-9_\-]+$/.test(key);
}

/**
 * Validate publisher ID: non-empty alphanumeric / dash / underscore.
 */
export function isValidPublisherId(id: string): boolean {
	if (typeof id !== "string") return false;
	if (id.length === 0) return false;
	return /^[A-Za-z0-9_\-]+$/.test(id);
}

export const awinIntegrationApiKeyPublisherIdQueryKeys = {
	all: ["awin-integration-api-key-publisher-id"] as const,
	list: () => [...awinIntegrationApiKeyPublisherIdQueryKeys.all, "list"] as const,
	detail: (publisher_id: string) =>
		[...awinIntegrationApiKeyPublisherIdQueryKeys.all, publisher_id] as const,
};
